import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Paper,
    IconButton,
    Tooltip,
    Avatar,
    Tab,
    Tabs,
    Select,
    MenuItem,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputLabel,
    FormControlLabel,
    Switch,
    CircularProgress,
    Chip,
    Snackbar
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import AffiliateDashboard from '../../components/affiliate/AffiliateDashboard';
import api, { authAPI, affiliateAPI, flexProfileAPI } from '../../services/api';
import BoltIcon from '@mui/icons-material/Bolt';
import { trackFlexEvent, FLEX_EVENTS } from '../../utils/flexAnalytics';

const profileValidationSchema = Yup.object({
    bio: Yup.string()
        .max(500, 'Bio must be less than 500 characters')
});

const passwordValidationSchema = Yup.object({
    currentPassword: Yup.string()
        .required('Current password is required'),
    newPassword: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]/,
            'Password must contain at least one letter, one number and one special character'
        )
        .required('New password is required'),
    confirmNewPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Confirm password is required')
});

const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English' }, { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' }, { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' }, { code: 'it', name: 'Italian' },
    { code: 'nl', name: 'Dutch' }, { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' }, { code: 'zh', name: 'Chinese' },
    { code: 'ar', name: 'Arabic' }, { code: 'hi', name: 'Hindi' },
    { code: 'ru', name: 'Russian' }, { code: 'tr', name: 'Turkish' },
    { code: 'pl', name: 'Polish' }, { code: 'sv', name: 'Swedish' }
];

const Profile = () => {
    const { user, updateProfile, changePassword, uploadAvatar, refreshUserData } = useAuth();
    const [searchParams] = useSearchParams();
    const [profileError, setProfileError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const [avatarError, setAvatarError] = useState('');
    const [avatarSuccess, setAvatarSuccess] = useState('');
    const [activeTab, setActiveTab] = useState(() => {
        const tabParam = searchParams.get('tab');
        return tabParam ? parseInt(tabParam, 10) : 0;
    });
    const [affiliateStats, setAffiliateStats] = useState(null);
    const [preferredLanguage, setPreferredLanguage] = useState(user?.profile?.preferredLanguage || 'en');
    const [pronunciationEnabled, setPronunciationEnabled] = useState(user?.profile?.pronunciationGuide?.enabled || false);
    const [pauseTimer, setPauseTimer] = useState(user?.profile?.pronunciationGuide?.pauseTimer || false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
    const [deletingAccount, setDeletingAccount] = useState(false);
    const fileInputRef = useRef();

    // Handle change
    const [handleEligibility, setHandleEligibility] = useState(null);
    const [handleEligibilityLoading, setHandleEligibilityLoading] = useState(false);
    const [newHandleInput, setNewHandleInput] = useState('');
    const [handleChangeError, setHandleChangeError] = useState('');
    const [handleChangeSuccess, setHandleChangeSuccess] = useState('');
    const [handleChanging, setHandleChanging] = useState(false);
    const [handleUnlocking, setHandleUnlocking] = useState(false);

    // FLEX Profile settings
    const [flexSettings, setFlexSettings] = useState(null);
    const [flexSettingsLoading, setFlexSettingsLoading] = useState(false);
    const [flexSettingsError, setFlexSettingsError] = useState('');
    const [flexSettingsSuccess, setFlexSettingsSuccess] = useState('');
    const [flexCards, setFlexCards] = useState([]);
    const [flexSnackbar, setFlexSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [bioDraft, setBioDraft] = useState('');
    const [bioSaving, setBioSaving] = useState(false);

    useEffect(() => {
        // Handle Stripe redirect for handle unlock
        const handleUnlocked = searchParams.get('handle_unlocked');
        if (handleUnlocked === 'true') {
            const sessionId = searchParams.get('session_id');
            if (sessionId) {
                authAPI.confirmHandleUnlock(sessionId)
                    .then(() => {
                        setHandleChangeSuccess('Payment confirmed! You can now change your username.');
                        authAPI.checkHandleEligibility()
                            .then(data => setHandleEligibility(data))
                            .catch(() => {});
                    })
                    .catch(err => {
                        setHandleChangeError(err.response?.data?.error || 'Failed to confirm payment.');
                    });
            }
        }
    }, [searchParams]);

    useEffect(() => {
        const fetchAffiliateStats = async () => {
            try {
                const response = await affiliateAPI.getStats();
                // Add username to the stats data for the affiliate link
                setAffiliateStats({
                    ...response,
                    username: user?.profile?.displayName || user?.username
                });
            } catch (error) {
                console.error('Failed to fetch affiliate stats:', error);
                // Set default empty data structure to prevent errors
                setAffiliateStats({
                    earnings: { total: 0, direct: 0, secondLevel: 0 },
                    referrals: [],
                    username: user?.profile?.displayName || user?.username
                });
            }
        };

        fetchAffiliateStats();
    }, [user]);

    useEffect(() => {
        if (activeTab === 2 && !flexSettings && !flexSettingsLoading) {
            fetchFlexSettings();
        }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch handle eligibility on profile tab
    useEffect(() => {
        if (activeTab === 0) {
            setHandleEligibilityLoading(true);
            authAPI.checkHandleEligibility()
                .then(data => setHandleEligibility(data))
                .catch(() => {})
                .finally(() => setHandleEligibilityLoading(false));
        }
    }, [activeTab]);

    const handleUnlockHandleChange = async () => {
        try {
            setHandleUnlocking(true);
            setHandleChangeError('');
            const data = await authAPI.unlockHandleChange();
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            setHandleChangeError(err.response?.data?.error || 'Failed to create checkout session.');
        } finally {
            setHandleUnlocking(false);
        }
    };

    const handleSubmitHandleChange = async () => {
        try {
            setHandleChanging(true);
            setHandleChangeError('');
            setHandleChangeSuccess('');
            const data = await authAPI.changeHandle(newHandleInput);
            setHandleChangeSuccess(`Your username is now @${data.displayName}. Your referral link and profile URL have been updated.`);
            setNewHandleInput('');
            // Refresh eligibility
            const elig = await authAPI.checkHandleEligibility();
            setHandleEligibility(elig);
            // Refresh user data in context
            if (refreshUserData) await refreshUserData();
        } catch (err) {
            setHandleChangeError(err.response?.data?.error || 'Failed to change handle.');
        } finally {
            setHandleChanging(false);
        }
    };

    const fetchFlexSettings = async () => {
        try {
            setFlexSettingsLoading(true);
            const [settingsRes, cardsRes] = await Promise.all([
                flexProfileAPI.getSettings(),
                flexProfileAPI.getMyCards(),
            ]);
            setFlexSettings(settingsRes);
            setFlexCards(cardsRes.cards || []);
            setBioDraft(settingsRes.publicProfile?.bio || '');
        } catch (err) {
            console.error('Failed to fetch FLEX settings:', err);
        } finally {
            setFlexSettingsLoading(false);
        }
    };

    const handleFlexSettingChange = async (category, field, value) => {
        const prevSettings = flexSettings;

        // Optimistic update — toggle moves immediately
        setFlexSettings(prev => ({
            ...prev,
            [category]: { ...prev[category], [field]: value },
        }));

        try {
            const payload = {};
            if (category === 'socialPreferences') {
                payload.socialPreferences = { [field]: value };
            } else if (category === 'publicProfile') {
                payload.publicProfile = { [field]: value };
            }

            const res = await flexProfileAPI.updateSettings(payload);
            setFlexSettings(prev => ({
                ...prev,
                socialPreferences: res.socialPreferences,
                publicProfile: res.publicProfile,
            }));

            if (field === 'publicProfileEnabled' && value === true) {
                trackFlexEvent(FLEX_EVENTS.PROFILE_ENABLED);
            } else if (field === 'publicProfileEnabled' && value === false) {
                trackFlexEvent(FLEX_EVENTS.PROFILE_DISABLED);
            }

            setFlexSnackbar({ open: true, message: 'Setting saved.', severity: 'success' });
        } catch (err) {
            // Revert on failure
            setFlexSettings(prevSettings);
            const detail = err.response?.data?.detail;
            const msg = detail ? `${err.response?.data?.error || 'Failed to update settings.'} (${detail})` : err.response?.data?.error || 'Failed to update settings.';
            setFlexSettingsError(msg);
            setFlexSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const handleBioSave = async () => {
        try {
            setBioSaving(true);
            setFlexSettingsError('');
            setFlexSettingsSuccess('');
            const res = await flexProfileAPI.updateSettings({ publicProfile: { bio: bioDraft } });
            setFlexSettings(prev => ({
                ...prev,
                publicProfile: res.publicProfile,
            }));
            setFlexSettingsSuccess('Bio saved.');
            setFlexSnackbar({ open: true, message: 'Bio saved.', severity: 'success' });
            setTimeout(() => setFlexSettingsSuccess(''), 3000);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to save bio.';
            setFlexSettingsError(msg);
            setFlexSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setBioSaving(false);
        }
    };

    const handleCardVisibilityToggle = async (cardId, currentVisibility) => {
        const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
        try {
            await flexProfileAPI.setCardVisibility(cardId, newVisibility);
            setFlexCards(prev => prev.map(c =>
                c._id === cardId ? { ...c, visibility: newVisibility } : c
            ));
        } catch (err) {
            console.error('Failed to toggle card visibility:', err);
        }
    };

    const handleDeleteAccount = async () => {
        setDeletingAccount(true);
        try {
            await api.delete('/auth/account', { data: { confirmEmail: deleteConfirmEmail } });
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/';
        } catch (err) {
            console.error('Account deletion failed:', err);
            setDeletingAccount(false);
        }
    };

    const handleExportData = async () => {
        try {
            const response = await api.get('/auth/export-data');
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `readerflex-data-export-${Date.now()}.json`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Data export failed:', err);
        }
    };

    const handleAvatarChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setAvatarError('Please upload an image file');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setAvatarError('Image size should be less than 5MB');
            return;
        }

        try {
            await uploadAvatar(file);
            setAvatarSuccess('Avatar updated successfully');
            setAvatarError('');
        } catch (err) {
            setAvatarError(err.error || 'Failed to upload avatar');
            setAvatarSuccess('');
        }
    };

    const handleProfileSubmit = async (values, { setSubmitting }) => {
        try {
            await updateProfile({
                profile: {
                    bio: values.bio,
                    preferredLanguage,
                    pronunciationGuide: {
                        enabled: pronunciationEnabled,
                        pauseTimer
                    }
                }
            });
            setProfileSuccess('Profile updated successfully');
            setProfileError('');
        } catch (err) {
            setProfileError(err.response?.data?.error || 'Failed to update profile');
            setProfileSuccess('');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePasswordSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            await changePassword(values.currentPassword, values.newPassword);
            setPasswordSuccess('Password updated successfully');
            setPasswordError('');
            resetForm();
        } catch (err) {
            setPasswordError(err.response?.data?.error || 'Failed to change password');
            setPasswordSuccess('');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyClick = async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}?ref=${user.profile.displayName}`);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ width: '100%', typography: 'body1', mt: 4 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                        <Tab label="Profile Settings" />
                        <Tab label="Affiliate Dashboard" />
                        <Tab label="FLEX Profile" />
                    </Tabs>
                </Box>

                {activeTab === 0 ? (
                    <>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            {/* Avatar Section */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar
                                    src={user?.profile?.avatarUrl}
                                    sx={{ width: 100, height: 100, mr: 2 }}
                                >
                                    {(user?.profile?.displayName || user?.username || 'U')[0].toUpperCase()}
                                </Avatar>
                                <Box>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        ref={fileInputRef}
                                        onChange={handleAvatarChange}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<PhotoCamera />}
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        Change Avatar
                                    </Button>
                                    {avatarError && (
                                        <Alert severity="error" sx={{ mt: 1 }}>
                                            {avatarError}
                                        </Alert>
                                    )}
                                    {avatarSuccess && (
                                        <Alert severity="success" sx={{ mt: 1 }}>
                                            {avatarSuccess}
                                        </Alert>
                                    )}
                                </Box>
                            </Box>

                            {/* Profile Form */}
                            <Typography variant="h5" gutterBottom>
                                Profile Information
                            </Typography>
                            <Formik
                                initialValues={{
                                    bio: user?.profile?.bio || ''
                                }}
                                validationSchema={profileValidationSchema}
                                onSubmit={handleProfileSubmit}
                            >
                                {({ values, handleChange, handleBlur, touched, errors }) => (
                                    <Form>
                                        <TextField
                                            fullWidth
                                            id="bio"
                                            name="bio"
                                            label="Bio"
                                            multiline
                                            rows={4}
                                            value={values.bio}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.bio && Boolean(errors.bio)}
                                            helperText={touched.bio && errors.bio}
                                            sx={{ mb: 3 }}
                                        />

                                        {/* Language & Pronunciation Settings */}
                                        <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>Reading Preferences</Typography>
                                        <FormControl fullWidth sx={{ mb: 2 }}>
                                            <InputLabel>Article Language</InputLabel>
                                            <Select
                                                value={preferredLanguage}
                                                label="Article Language"
                                                onChange={(e) => setPreferredLanguage(e.target.value)}
                                            >
                                                {SUPPORTED_LANGUAGES.map(lang => (
                                                    <MenuItem key={lang.code} value={lang.code}>{lang.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={pronunciationEnabled}
                                                    onChange={(e) => setPronunciationEnabled(e.target.checked)}
                                                />
                                            }
                                            label="Show pronunciation guide for difficult words"
                                            sx={{ mb: 1, display: 'block' }}
                                        />
                                        {pronunciationEnabled && (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={pauseTimer}
                                                        onChange={(e) => setPauseTimer(e.target.checked)}
                                                    />
                                                }
                                                label="Pause reading timer when viewing pronunciation"
                                                sx={{ mb: 2, ml: 2, display: 'block' }}
                                            />
                                        )}

                                        <Button type="submit" variant="contained" color="primary">
                                            Update Profile
                                        </Button>
                                        {profileError && (
                                            <Alert severity="error" sx={{ mt: 2 }}>
                                                {profileError}
                                            </Alert>
                                        )}
                                        {profileSuccess && (
                                            <Alert severity="success" sx={{ mt: 2 }}>
                                                {profileSuccess}
                                            </Alert>
                                        )}
                                    </Form>
                                )}
                            </Formik>
                        </Paper>

                        {/* Handle Change Section — only visible to eligible users */}
                        {handleEligibilityLoading ? null : handleEligibility?.eligible && (
                            <Paper sx={{ p: 3, mb: 3 }}>
                                <Typography variant="h5" gutterBottom>
                                    Change Username
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Your current username is <strong>@{handleEligibility.currentHandle}</strong>.
                                    {handleEligibility.freeChangeAvailable
                                        ? ' Your first change is free.'
                                        : ' Additional changes cost $2.50 with a 30-day cooldown.'}
                                </Typography>
                                {handleEligibility.isAdmin && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        As an admin, you can change your username anytime at no cost.
                                    </Typography>
                                )}
                                <TextField
                                    fullWidth
                                    label="New username"
                                    value={newHandleInput}
                                    onChange={(e) => setNewHandleInput(e.target.value)}
                                    helperText="3-30 characters. Letters, numbers, underscores, and hyphens only."
                                    sx={{ mb: 2 }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmitHandleChange}
                                        disabled={handleChanging || !newHandleInput || newHandleInput.length < 3}
                                    >
                                        {handleChanging ? <CircularProgress size={24} /> : 'Change Username'}
                                    </Button>
                                    {!handleEligibility.freeChangeAvailable && handleEligibility.needsPayment && (
                                        <Button
                                            variant="outlined"
                                            onClick={handleUnlockHandleChange}
                                            disabled={handleUnlocking}
                                        >
                                            {handleUnlocking ? <CircularProgress size={24} /> : 'Unlock for $2.50'}
                                        </Button>
                                    )}
                                </Box>
                                {handleChangeError && (
                                    <Alert severity="error" sx={{ mt: 2 }}>{handleChangeError}</Alert>
                                )}
                                {handleChangeSuccess && (
                                    <Alert severity="success" sx={{ mt: 2 }}>{handleChangeSuccess}</Alert>
                                )}
                            </Paper>
                        )}

                        {/* Password Change Form */}
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h5" gutterBottom>
                                Change Password
                            </Typography>
                            <Formik
                                initialValues={{
                                    currentPassword: '',
                                    newPassword: '',
                                    confirmNewPassword: ''
                                }}
                                validationSchema={passwordValidationSchema}
                                onSubmit={handlePasswordSubmit}
                            >
                                {({ values, handleChange, handleBlur, touched, errors }) => (
                                    <Form>
                                        <TextField
                                            fullWidth
                                            id="currentPassword"
                                            name="currentPassword"
                                            label="Current Password"
                                            type="password"
                                            value={values.currentPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.currentPassword && Boolean(errors.currentPassword)}
                                            helperText={touched.currentPassword && errors.currentPassword}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            id="newPassword"
                                            name="newPassword"
                                            label="New Password"
                                            type="password"
                                            value={values.newPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.newPassword && Boolean(errors.newPassword)}
                                            helperText={touched.newPassword && errors.newPassword}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            id="confirmNewPassword"
                                            name="confirmNewPassword"
                                            label="Confirm New Password"
                                            type="password"
                                            value={values.confirmNewPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.confirmNewPassword && Boolean(errors.confirmNewPassword)}
                                            helperText={touched.confirmNewPassword && errors.confirmNewPassword}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button type="submit" variant="contained" color="primary">
                                            Change Password
                                        </Button>
                                        {passwordError && (
                                            <Alert severity="error" sx={{ mt: 2 }}>
                                                {passwordError}
                                            </Alert>
                                        )}
                                        {passwordSuccess && (
                                            <Alert severity="success" sx={{ mt: 2 }}>
                                                {passwordSuccess}
                                            </Alert>
                                        )}
                                    </Form>
                                )}
                            </Formik>
                        </Paper>

                        {/* Affiliate Section */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h5" gutterBottom>
                                Affiliate Program
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body1" gutterBottom>
                                    Share your unique referral link to earn rewards:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        value={`${window.location.origin}?ref=${user?.profile?.displayName || ''}`}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                    <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"}>
                                        <IconButton onClick={handleCopyClick}>
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Earn commissions on referral subscriptions:
                                </Typography>
                                <Box component="ul" sx={{ 
                                    color: theme => theme.palette.text.secondary,
                                    typography: 'body2',
                                    pl: 4,
                                    mb: 2
                                }}>
                                    <li>$3.00/month for direct monthly referral subscriptions</li>
                                    <li>$24.00/year for direct annual referral subscriptions</li>
                                    <li>$1.00/month or $8.00/year for second-level referrals</li>
                                    <li>Withdrawals available once commissions clear the 30-day holding period (minimum $50)</li>
                                </Box>
                                {affiliateStats && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1" color="primary" gutterBottom>
                                            Quick Stats:
                                        </Typography>
                                        <Typography variant="body2">
                                            • Total Referrals: {affiliateStats.referrals?.length || 0}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Active Subscribers: {affiliateStats.referrals?.filter(ref => ref.user.subscription?.status === 'active').length || 0}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Total Earnings: ${affiliateStats.earnings?.total?.toFixed(2) || '0.00'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </>
                ) : activeTab === 1 ? (
                    <AffiliateDashboard affiliateStats={affiliateStats} />
                ) : (
                    /* ═══════════ TAB 2: FLEX PROFILE ═══════════ */
                    <>
                        {flexSettingsLoading && !flexSettings ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                        ) : !flexSettings ? (
                            <Alert severity="info">Unable to load FLEX profile settings.</Alert>
                        ) : (
                            <>
                                {/* Enable / Disable Public Profile */}
                                <Paper sx={{ p: 3, mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <BoltIcon sx={{ color: '#FFD700' }} />
                                        <Typography variant="h5">FLEX Public Profile</Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Your FLEX profile lives at <strong>/flex/{flexSettings.handle}</strong>. It's private by default — enable it to share your reading achievements with the world.
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={flexSettings.socialPreferences?.publicProfileEnabled || false}
                                                onChange={(e) => handleFlexSettingChange('socialPreferences', 'publicProfileEnabled', e.target.checked)}
                                            />
                                        }
                                        label={flexSettings.socialPreferences?.publicProfileEnabled ? 'Public profile enabled' : 'Enable public profile'}
                                        sx={{ mb: 2 }}
                                    />
                                    {flexSettings.socialPreferences?.publicProfileEnabled && (
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={`${window.location.origin}/flex/${flexSettings.handle}`}
                                                    InputProps={{ readOnly: true }}
                                                />
                                                <Tooltip title="Copy to clipboard">
                                                    <IconButton onClick={() => {
                                                        navigator.clipboard.writeText(`${window.location.origin}/flex/${flexSettings.handle}`);
                                                        setCopySuccess(true);
                                                        setTimeout(() => setCopySuccess(false), 2000);
                                                    }}>
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            {copySuccess && <Typography variant="caption" color="success.main">Copied!</Typography>}
                                            {flexSettings.publicProfile?.publishedAt && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    Profile published {new Date(flexSettings.publicProfile.publishedAt).toLocaleDateString()}
                                                    {flexSettings.publicProfile.viewCount > 0 && ` · ${flexSettings.publicProfile.viewCount} views`}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Paper>

                                {/* Visibility Controls */}
                                {flexSettings.socialPreferences?.publicProfileEnabled && (
                                    <>
                                        <Paper sx={{ p: 3, mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>Public Bio</Typography>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                placeholder="Tell the world about your reading journey..."
                                                value={bioDraft}
                                                onChange={(e) => setBioDraft(e.target.value)}
                                                helperText="Max 500 characters"
                                                sx={{ mb: 2 }}
                                            />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleBioSave}
                                                    disabled={bioSaving || bioDraft === (flexSettings.publicProfile?.bio || '')}
                                                >
                                                    {bioSaving ? <CircularProgress size={20} /> : 'Save Bio'}
                                                </Button>
                                                {bioDraft !== (flexSettings.publicProfile?.bio || '') && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Unsaved changes
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Paper>

                                        <Paper sx={{ p: 3, mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>What to Show</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Control which stats are visible on your public profile. Changes save automatically.
                                            </Typography>
                                            {[
                                                { field: 'showWPM', label: 'WPM (words per minute)' },
                                                { field: 'showComprehension', label: 'Comprehension scores' },
                                                { field: 'showEffectiveWPM', label: 'Effective WPM' },
                                                { field: 'showStreak', label: 'Reading streak' },
                                                { field: 'showBadges', label: 'Badges' },
                                                { field: 'showLanguages', label: 'Preferred languages' },
                                                { field: 'showProgressChart', label: 'Progress stats (words read, articles, time)' },
                                            ].map(({ field, label }) => (
                                                <FormControlLabel
                                                    key={field}
                                                    control={
                                                        <Switch
                                                            checked={flexSettings.socialPreferences?.[field] ?? false}
                                                            onChange={(e) => handleFlexSettingChange('socialPreferences', field, e.target.checked)}
                                                        />
                                                    }
                                                    label={label}
                                                    sx={{ display: 'block', mb: 0.5 }}
                                                />
                                            ))}
                                        </Paper>

                                        <Paper sx={{ p: 3, mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>Privacy & SEO</Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={flexSettings.socialPreferences?.profileIndexable ?? false}
                                                        onChange={(e) => handleFlexSettingChange('socialPreferences', 'profileIndexable', e.target.checked)}
                                                    />
                                                }
                                                label="Allow search engines to index my profile"
                                                sx={{ display: 'block', mb: 1 }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={flexSettings.socialPreferences?.leaderboardParticipation !== 'off'}
                                                        onChange={(e) => handleFlexSettingChange('socialPreferences', 'leaderboardParticipation', e.target.checked ? 'public' : 'off')}
                                                    />
                                                }
                                                label="Participate in public leaderboards"
                                                sx={{ display: 'block', mb: 1 }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={flexSettings.socialPreferences?.challengeInvitations ?? true}
                                                        onChange={(e) => handleFlexSettingChange('socialPreferences', 'challengeInvitations', e.target.checked)}
                                                    />
                                                }
                                                label="Accept challenge invitations from other users"
                                                sx={{ display: 'block' }}
                                            />
                                        </Paper>

                                        {/* Card Gallery Management */}
                                        <Paper sx={{ p: 3, mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>FLEX Card Gallery</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Choose which FLEX Cards appear on your public profile.
                                            </Typography>
                                            {flexCards.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    No FLEX Cards yet. Complete a reading session to generate one!
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {flexCards.map((card) => (
                                                        <Box
                                                            key={card._id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                p: 1.5,
                                                                borderRadius: 1,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                            }}
                                                        >
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Chip
                                                                    label={card.cardType}
                                                                    size="small"
                                                                    sx={{ mr: 1, textTransform: 'capitalize' }}
                                                                />
                                                                <Typography variant="body2" component="span" noWrap sx={{ fontWeight: 600 }}>
                                                                    {card.headline}
                                                                </Typography>
                                                            </Box>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={card.visibility === 'public'}
                                                                        onChange={() => handleCardVisibilityToggle(card._id, card.visibility)}
                                                                        size="small"
                                                                    />
                                                                }
                                                                label={card.visibility === 'public' ? 'Public' : 'Private'}
                                                                labelPlacement="start"
                                                                sx={{ ml: 1, flexShrink: 0 }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Paper>
                                    </>
                                )}

                                {flexSettingsError && <Alert severity="error" sx={{ mb: 2 }}>{flexSettingsError}</Alert>}
                                {flexSettingsSuccess && <Alert severity="success" sx={{ mb: 2 }}>{flexSettingsSuccess}</Alert>}
                                <Snackbar
                                    open={flexSnackbar.open}
                                    autoHideDuration={3000}
                                    onClose={() => setFlexSnackbar({ ...flexSnackbar, open: false })}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                >
                                    <Alert
                                        severity={flexSnackbar.severity}
                                        onClose={() => setFlexSnackbar({ ...flexSnackbar, open: false })}
                                    >
                                        {flexSnackbar.message}
                                    </Alert>
                                </Snackbar>
                            </>
                        )}
                    </>
                )}
            </Box>

            {/* Data & Privacy Section */}
            <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>Data &amp; Privacy</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Export your data or delete your account. These actions are permanent.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        onClick={handleExportData}
                    >
                        Export My Data
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        Delete Account
                    </Button>
                    <Button
                        variant="text"
                        href="/email-preferences"
                    >
                        Email Preferences
                    </Button>
                </Box>
            </Paper>

            {/* Delete Account Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete your account?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will permanently delete all your data: reading stats, badges, vocabulary, and subscription history. This cannot be undone.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Type your email address to confirm:
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={deleteConfirmEmail}
                        onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                        placeholder={user?.email}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleteConfirmEmail !== user?.email || deletingAccount}
                        onClick={handleDeleteAccount}
                    >
                        {deletingAccount ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Profile;
